import { useState } from 'react';
import { Head } from '@inertiajs/react';
import {
    Box,
    Typography,
    CardContent,
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    TextField,
    Snackbar,
    Alert
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import MainLayout from '../../components/MainLayout';
import { PrimaryButton, SecondaryButton, CustomCard } from '../../components';
import CustomDatePicker from '../../components/CustomDatePicker';
import axiosClient from '../../services/axiosClient';

export interface Roommate {
    id: string;
    name: string;
    email?: string;
    move_in_date: string;
    move_out_date?: string;
    is_admin?: boolean;
}

interface UtilRoomiesProps {
    current_roommates?: Roommate[];
    past_roommates?: Roommate[];
    household_id?: string;
    is_customer?: boolean;
    current_roommate_is_admin?: boolean;
}

function formatDate(dateStr: string): string {
    const d = new Date(dateStr + 'T12:00:00');
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function UtilRoomies(props: UtilRoomiesProps) {
    const [currentRoommates, setCurrentRoommates] = useState<Roommate[]>(props.current_roommates ?? []);
    const [pastRoommates] = useState<Roommate[]>(props.past_roommates ?? []);
    const canEdit = props.is_customer || props.current_roommate_is_admin;

    const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
    const [_removeRoommateId, setRemoveRoommateId] = useState<string | null>(null);
    const [moveOutDate, setMoveOutDate] = useState<Date | null>(null);

    const [addDialogOpen, setAddDialogOpen] = useState(false);
    const [addName, setAddName] = useState('');
    const [addEmail, setAddEmail] = useState('');
    const [addMoveInDate, setAddMoveInDate] = useState<Date | null>(null);
    const [creatingRoommate, setCreatingRoommate] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

    const [leaveHouseholdOpen, setLeaveHouseholdOpen] = useState(false);
    const [leaveMoveOutDate, setLeaveMoveOutDate] = useState<Date | null>(null);

    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editRoommateId, setEditRoommateId] = useState<string | null>(null);
    const [editRoommateName, setEditRoommateName] = useState('');
    const [editEmail, setEditEmail] = useState('');
    const [editMoveInDate, setEditMoveInDate] = useState<Date | null>(null);
    const [editMoveOutDate, setEditMoveOutDate] = useState<Date | null>(null);
    const [editLoading, setEditLoading] = useState(false);

    const handleOpenRemove = (id: string) => {
        setRemoveRoommateId(id);
        setMoveOutDate(null);
        setRemoveDialogOpen(true);
    };

    const handleCloseRemove = () => {
        setRemoveDialogOpen(false);
        setRemoveRoommateId(null);
        setMoveOutDate(null);
    };

    const handleConfirmRemove = () => {
        // TODO: submit move-out to backend
        handleCloseRemove();
    };

    const handleOpenAdd = () => {
        setAddName('');
        setAddEmail('');
        setAddMoveInDate(null);
        setAddDialogOpen(true);
    };

    const handleCloseAdd = () => {
        setAddDialogOpen(false);
        setAddName('');
        setAddEmail('');
        setAddMoveInDate(null);
    };

    const handleConfirmAdd = () => {
        if (!props.household_id || !addMoveInDate || !addName.trim()) return;

        setCreatingRoommate(true);
        axiosClient.post(`/util/dashboard/${props.household_id}/roomies`, {
            name: addName.trim(),
            email: addEmail.trim() || undefined,
            move_in_date: addMoveInDate.toISOString().split('T')[0]
        })
            .then((response) => {
                const roommate = response.data?.roommate;
                if (roommate) {
                    setCurrentRoommates((prev) => [roommate, ...prev]);
                }
                setSnackbarMessage('Roommate added successfully!');
                setSnackbarSeverity('success');
                setSnackbarOpen(true);
                handleCloseAdd();
            })
            .catch((error: any) => {
                setSnackbarMessage(error?.response?.data?.error || 'Failed to add roommate');
                setSnackbarSeverity('error');
                setSnackbarOpen(true);
            })
            .finally(() => setCreatingRoommate(false));
    };

    const handleCloseSnackbar = () => {
        setSnackbarOpen(false);
    };

    const handleOpenLeaveHousehold = () => {
        setLeaveMoveOutDate(null);
        setLeaveHouseholdOpen(true);
    };
    const handleCloseLeaveHousehold = () => {
        setLeaveHouseholdOpen(false);
        setLeaveMoveOutDate(null);
    };
    const handleConfirmLeaveHousehold = () => {
        // TODO: submit leave household to backend
        handleCloseLeaveHousehold();
    };

    const handleOpenEdit = (roommate: Roommate) => {
        setEditRoommateId(roommate.id);
        setEditRoommateName(roommate.name);
        setEditEmail(roommate.email ?? '');
        setEditMoveInDate(roommate.move_in_date ? new Date(roommate.move_in_date + 'T12:00:00') : null);
        setEditMoveOutDate(roommate.move_out_date ? new Date(roommate.move_out_date + 'T12:00:00') : null);
        setEditDialogOpen(true);
    };

    const handleCloseEdit = () => {
        setEditDialogOpen(false);
        setEditRoommateId(null);
        setEditRoommateName('');
        setEditEmail('');
        setEditMoveInDate(null);
        setEditMoveOutDate(null);
        setEditLoading(false);
    };

    const handleConfirmEdit = async () => {
        if (!editRoommateId || !editMoveInDate) return;

        setEditLoading(true);
        try {
            const moveInStr = editMoveInDate.toISOString().split('T')[0];
            const moveOutStr = editMoveOutDate ? editMoveOutDate.toISOString().split('T')[0] : null;

            await axiosClient.patch(`/util/dashboard/${props.household_id}/roommates/${editRoommateId}`, {
                email: editEmail.trim() || null,
                move_in_date: moveInStr,
                move_out_date: moveOutStr
            });

            setSnackbarMessage('Roommate updated successfully!');
            setSnackbarSeverity('success');
            setSnackbarOpen(true);
            handleCloseEdit();
            window.location.reload();
        } catch (error) {
            setSnackbarMessage('Failed to update roommate');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            setEditLoading(false);
        }
    };

    return (
        <>
            <Head title="Roomies - HeyyProperty" />
            <MainLayout
                headerButton={
                    <SecondaryButton onClick={handleOpenLeaveHousehold}>
                        Leave Household
                    </SecondaryButton>
                }
                utilDashboard
                householdId={props.household_id}
            >
                {/* Current roommates card */}
                <CustomCard>
                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                        <Typography fontWeight="bold" variant="h6" sx={{ mb: 2 }}>
                            Current Roommates
                        </Typography>
                        {currentRoommates.length === 0 ? (
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                No roommates yet.
                            </Typography>
                        ) : (
                            currentRoommates.map((r) => (
                                <Box
                                    key={r.id}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        py: 1.5,
                                        borderBottom: '1px solid',
                                        borderColor: 'grey.200'
                                    }}
                                >
                                    <Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography variant="body1" fontWeight={500}>
                                                {r.name}
                                            </Typography>
                                            {r.is_admin && (
                                                <Typography
                                                    component="span"
                                                    variant="caption"
                                                    sx={{
                                                        px: 1.5,
                                                        py: 0.25,
                                                        borderRadius: 1,
                                                        bgcolor: 'black',
                                                        color: 'white',
                                                        fontWeight: 800
                                                    }}
                                                >
                                                    Admin
                                                </Typography>
                                            )}
                                        </Box>
                                        <Typography variant="body2" color="text.secondary">
                                            Move-in: {formatDate(r.move_in_date)}
                                            {r.email && ` · ${r.email}`}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        {canEdit && (
                                            <IconButton
                                                size="small"
                                                onClick={() => handleOpenEdit(r)}
                                                sx={{
                                                    border: '1px solid',
                                                    borderColor: 'grey.300',
                                                    borderRadius: '8px',
                                                    p: 1
                                                }}
                                            >
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                        )}
                                        <SecondaryButton size="small" onClick={() => handleOpenRemove(r.id)}>
                                            Remove
                                        </SecondaryButton>
                                    </Box>
                                </Box>
                            ))
                        )}
                        {canEdit && (
                            <PrimaryButton fullWidth onClick={handleOpenAdd} sx={{ mt: 2 }}>
                                Add new roommate
                            </PrimaryButton>
                        )}
                    </CardContent>
                </CustomCard>

                {/* Past roommates */}
                <CustomCard>
                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                        <Typography fontWeight="bold" variant="h6" sx={{ mb: 2 }}>
                            Past Roommates
                        </Typography>
                        {pastRoommates.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">
                                No past roommates.
                            </Typography>
                        ) : (
                            pastRoommates.map((r) => (
                                <Box
                                    key={r.id}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        py: 1.5,
                                        borderBottom: '1px solid',
                                        borderColor: 'grey.200'
                                    }}
                                >
                                    <Box>
                                        <Typography variant="body1" fontWeight={500}>
                                            {r.name}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Move-in: {formatDate(r.move_in_date)}
                                            {r.move_out_date && ` · Move-out: ${formatDate(r.move_out_date)}`}
                                            {r.email && ` · ${r.email}`}
                                        </Typography>
                                    </Box>
                                    {canEdit && (
                                        <IconButton
                                            size="small"
                                            onClick={() => handleOpenEdit(r)}
                                            sx={{
                                                border: '1px solid',
                                                borderColor: 'grey.300',
                                                borderRadius: '8px',
                                                p: 1
                                            }}
                                        >
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                    )}
                                </Box>
                            ))
                        )}
                    </CardContent>
                </CustomCard>

                {/* Remove roommate – move-out date dialog */}
                <Dialog
                    open={removeDialogOpen}
                    onClose={handleCloseRemove}
                    maxWidth="sm"
                    fullWidth
                    PaperProps={{ sx: { borderRadius: '16px' } }}
                >
                    <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            Set move-out date
                        </Typography>
                        <IconButton onClick={handleCloseRemove} size="small">
                            <CloseIcon />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent sx={{ pt: 1 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Select the date this roommate moved out.
                        </Typography>
                        <CustomDatePicker
                            label="Move-out date"
                            value={moveOutDate}
                            onChange={setMoveOutDate}
                            minDate={null}
                        />
                        <Box sx={{ display: 'flex', gap: 1, mt: 3 }}>
                            <SecondaryButton fullWidth onClick={handleCloseRemove}>
                                Cancel
                            </SecondaryButton>
                            <PrimaryButton fullWidth onClick={handleConfirmRemove} disabled={!moveOutDate}>
                                Confirm
                            </PrimaryButton>
                        </Box>
                    </DialogContent>
                </Dialog>

                {/* Add new roommate dialog */}
                <Dialog
                    open={addDialogOpen}
                    onClose={handleCloseAdd}
                    maxWidth="sm"
                    fullWidth
                    PaperProps={{ sx: { borderRadius: '16px' } }}
                >
                    <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            Add new roommate
                        </Typography>
                        <IconButton onClick={handleCloseAdd} size="small">
                            <CloseIcon />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent sx={{ pt: 1 }}>
                        <TextField
                            fullWidth
                            label="Name"
                            value={addName}
                            onChange={(e) => setAddName(e.target.value)}
                            sx={{ mb: 2 }}
                        />
                        <TextField
                            fullWidth
                            label="Email (optional)"
                            type="email"
                            value={addEmail}
                            onChange={(e) => setAddEmail(e.target.value)}
                            sx={{ mb: 2 }}
                        />
                        <CustomDatePicker
                            label="Move-in date"
                            value={addMoveInDate}
                            onChange={setAddMoveInDate}
                        />
                        <Box sx={{ display: 'flex', gap: 1, mt: 3 }}>
                            <SecondaryButton fullWidth onClick={handleCloseAdd}>
                                Cancel
                            </SecondaryButton>
                            <PrimaryButton
                                fullWidth
                                onClick={handleConfirmAdd}
                                disabled={!addName.trim() || !addMoveInDate || creatingRoommate}
                            >
                                {creatingRoommate ? 'Adding...' : 'Add'}
                            </PrimaryButton>
                        </Box>
                    </DialogContent>
                </Dialog>

                {/* Leave Household Dialog */}
                <Dialog
                    open={leaveHouseholdOpen}
                    onClose={handleCloseLeaveHousehold}
                    maxWidth="sm"
                    fullWidth
                    PaperProps={{ sx: { borderRadius: '16px' } }}
                >
                    <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            Leave Household
                        </Typography>
                        <IconButton onClick={handleCloseLeaveHousehold} size="small">
                            <CloseIcon />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent sx={{ pt: 1 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            When are you moving out?
                        </Typography>
                        <CustomDatePicker
                            label="Move-out date"
                            value={leaveMoveOutDate}
                            onChange={setLeaveMoveOutDate}
                            minDate={null}
                        />
                        <Box sx={{ display: 'flex', gap: 1, mt: 3 }}>
                            <SecondaryButton fullWidth onClick={handleCloseLeaveHousehold}>
                                Cancel
                            </SecondaryButton>
                            <PrimaryButton
                                fullWidth
                                onClick={handleConfirmLeaveHousehold}
                                disabled={!leaveMoveOutDate}
                            >
                                Leave Household
                            </PrimaryButton>
                        </Box>
                    </DialogContent>
                </Dialog>

                {/* Edit Roommate Dialog */}
                <Dialog
                    open={editDialogOpen}
                    onClose={handleCloseEdit}
                    maxWidth="sm"
                    fullWidth
                    PaperProps={{ sx: { borderRadius: '16px' } }}
                >
                    <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            Edit {editRoommateName}
                        </Typography>
                        <IconButton onClick={handleCloseEdit} size="small">
                            <CloseIcon />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent sx={{ pt: 1 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Update email and move-in / move-out dates for this roommate.
                        </Typography>
                        <TextField
                            fullWidth
                            label="Email"
                            type="email"
                            value={editEmail}
                            onChange={(e) => setEditEmail(e.target.value)}
                            sx={{ mb: 2 }}
                        />
                        <Box sx={{ mb: 2 }}>
                            <CustomDatePicker
                                label="Move-in date"
                                value={editMoveInDate}
                                onChange={setEditMoveInDate}
                                minDate={null}
                            />
                        </Box>
                        <CustomDatePicker
                            label="Move-out date (optional)"
                            value={editMoveOutDate}
                            onChange={setEditMoveOutDate}
                            minDate={null}
                        />
                        <Box sx={{ display: 'flex', gap: 1, mt: 3 }}>
                            <SecondaryButton fullWidth onClick={handleCloseEdit} disabled={editLoading}>
                                Cancel
                            </SecondaryButton>
                            <PrimaryButton
                                fullWidth
                                onClick={handleConfirmEdit}
                                disabled={!editMoveInDate || editLoading}
                            >
                                {editLoading ? 'Saving...' : 'Save Changes'}
                            </PrimaryButton>
                        </Box>
                    </DialogContent>
                </Dialog>

                {/* Snackbar for feedback */}
                <Snackbar open={snackbarOpen} autoHideDuration={3000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                    <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: '100%' }}>
                        {snackbarMessage}
                    </Alert>
                </Snackbar>
            </MainLayout>
        </>
    );
}
